import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Cpu, Wifi, Eye, Share2, Minimize2, Calculator, Navigation, Radio, Battery, Brain, Activity, Search, Code, ArrowRight, X, FileText, MonitorPlay, Download, Info } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

// --- VITAL: PDF WORKER SETUP ---
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- DATA ---
export const allProjects = [
  {
    id: 1,
    title: "Smart Throttle Control for EVs",
    category: "Control Systems",
    desc: "Designing a gain-scheduled controller for Electric Vehicles using MATLAB/Simulink. Focuses on smoothing throttle response and implementing real-time control algorithms on microcontrollers.",
    icon: <Cpu size={24} className="text-emerald-500" />,
    image: `${process.env.PUBLIC_URL}/images/ev.webp`,
    ppt: `${process.env.PUBLIC_URL}/docs/smart_throttle.pdf`,
    pdf: `${process.env.PUBLIC_URL}/docs/Smart_Throttle_proposal.pdf`,
    mentors: "Harshul Kambhoj, V Mouliprassana",
    prof: "SR Sahoo"
  },
  {
    id: 2,
    title: "Natural Language Command & Control for UAVs",
    category: "Robotics & AI",
    desc: "Bridging Generative AI and Robotics. A system that uses LLMs to interpret natural language commands (e.g., 'fly a helix') and converts them into safe, executable flight paths for ArduPilot UAVs.",
    icon: <Brain size={24} className="text-rose-500" />,
    image: `${process.env.PUBLIC_URL}/images/uav.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/nlp_command_control_prop.pdf`,
    ppt: `${process.env.PUBLIC_URL}/docs/nlp_command_control_ppt.pdf`,
    mentors:"Pratyush Srivastava and Akul agarwal",
    prof:"Ketan Rajawat"
  },
   {
    id: 3,
    title: "Smart Digital Multimeter",
    category: "Embedded Systems",
    mentors: "Akshat Dhote, Adwaaiit Pande",
    desc: "An industry-grade digital multimeter with intelligent auto-ranging for R, L, and C measurements. Features ~98% accuracy and seamless data logging to a mobile app via OTG.",
    icon: <Activity size={24} className="text-lime-500" />,
    image: `${process.env.PUBLIC_URL}/images/multimeter.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/multimeter_baquer_prop.pdf`,
    prof:"Bacquer Mazhari"
  },
  {
    id: 4,
    title: "Meta-Learning for Wireless",
    category: "Deep Learning",
    mentors: "Vashi Diwakar, Hrushikesh Roop Avvari, Chaitanya Nitawe",
    desc: "A meta-learning framework (MAML) for rapid adaptation of deep networks in dynamic wireless environments. Enables few-shot learning for channel estimation and localization with minimal data.",
    icon: <Brain size={24} className="text-indigo-500" />,
    image: `${process.env.PUBLIC_URL}/images/maml.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/Meta-Learning_Rohit_prop.pdf`,
    prof:"Rohit Budhiraja",
    ppt:`${process.env.PUBLIC_URL}/docs/maml_ppt.pdf`
  },
  {
    id: 5,
    title: "Formation UAV Path Planning",
    category: "Robotics",
    mentors: "Somya Kumar, Aryan Mahesh Gupta",
    desc: "Simulating multi-UAV formations using algorithms like A* and RRT*. Focuses on obstacle avoidance, maintaining specific shapes (like alphabets), and optimizing trajectories for minimum time vs. energy.",
    icon: <Navigation size={24} className="text-sky-500" />,
    image: `${process.env.PUBLIC_URL}/images/uav_plan.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/uav_path_planning_prop.pdf`,
    ppt: `${process.env.PUBLIC_URL}/docs/uav planning_ppt.pdf`,
    prof:"Ketan Rajawat"
  },
  {
    id: 6,
    title: "P300 Real-Time EEG Speller",
    category: "Biomedical / BCI",
    desc: "A Brain-Computer Interface (BCI) speller that detects P300 signals from EEG data. Replaces standard pipelines with custom ML models (SVM, EEGNet) in OpenViBE to allow users to 'type' with their thoughts.",
    icon: <Brain size={24} className="text-pink-500" />,
    image: `${process.env.PUBLIC_URL}/images/eeg_speller.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/EEG Speller_ Nikunj_prop.pdf`,
    prof:"Nikunj Arunkumar Bhagat"
  },
  {
    id: 7,
    title: "Vision Transformer (ViT)",
    category: "Deep Learning",
    mentors: "Gaddam Sai Sujan, Shaik Abdul Sameer, Nayani Om Swarup",
    desc: "Implementing Vision Transformers (ViT) from scratch for image classification on CIFAR-10. Covers self-attention mechanisms, patch embeddings, and performance comparison with standard CNNs.",
    icon: <Eye size={24} className="text-purple-500" />,
    image: `${process.env.PUBLIC_URL}/images/vit.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/vision_transformer_prop.pdf`,
    ppt: `${process.env.PUBLIC_URL}/docs/Rajesh_Vision_ppt.pdf`,
    prof:"Rajesh Hedge"
  },
  {
    id: 8,
    title: "GNN Fraud Detection",
    category: "Machine Learning",
    mentors: "Kavin, Soham Panchal, Ayush Inamdar",
    desc: "A comprehensive journey from classical ML to Graph Neural Networks (GNNs). The final system models financial transactions as a graph to detect complex fraud rings and money laundering patterns.",
    icon: <Share2 size={24} className="text-orange-500" />,
    image: `${process.env.PUBLIC_URL}/images/gnn_fraud.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/GNN_Rajesh_Hegde_prop.pdf`,
    ppt: `${process.env.PUBLIC_URL}/docs/Rajesh_GNNs & Fraud Detection_ppt.pdf`,
    prof:"Rajesh Hedge"
  },
  {
    id: 22,
    title: "Deep Neural Network Compression",
    category: "Deep Learning",
    mentors: "Ayush Jha, Krish Jain, Sahil Pandey",
    desc: "A 3-stage compression pipeline (Pruning, Quantization, Huffman Coding) to reduce AlexNet model size by 9x (228MB to ~25MB) on CIFAR-10 with minimal accuracy loss. Focuses on efficient on-device AI.",
    icon: <Minimize2 size={24} className="text-amber-500" />,
    image: `${process.env.PUBLIC_URL}/images/dnnc.webp`,
    ppt: `${process.env.PUBLIC_URL}/docs/Koteshwar_DNN_Compression_ppt.pdf`,
    pdf: `${process.env.PUBLIC_URL}/docs/CV_Koteswar_prop.pdf`,
    prof:"Koteswar Rao Jerripothula"
  },
  {
    id: 23,
    title: "Neural Networks on FPGA",
    category: "VLSI/FPGA",
    mentors: "Aritra Kolay, Meher Narula, Nikhil Verma",
    desc: "Implementing a multi-layer neural network directly on FPGA hardware using Verilog. Covers RTL design of neurons, MAC units, activation functions, and performance analysis (latency/throughput).",
    icon: <Cpu size={24} className="text-cyan-600" />,
    image: `${process.env.PUBLIC_URL}/images/nnnfpga.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/Chithra_Neural_network_prop.pdf`,
    ppt: `${process.env.PUBLIC_URL}/docs/Chithra_NN on FPGAs_ppt.pdf`,
    prof:"Chithra"
  },
  {
    id: 24,
    title: "Semiconductor Transport Solver",
    category: "Scientific Computing",
    mentors: "Anirvan Tyagi, Dhakshith S, Mrigeesh, Sriniketh",
    prof: "Prof. Rituraj",
    desc: "Developing a numerical solver for semiconductor transport equations (Poisson & Drift-Diffusion) using DEVSIM. Involves 1D/2D device meshing, physics simulation, and visualizing carrier dynamics.",
    icon: <Calculator size={24} className="text-blue-500" />,
    image: `${process.env.PUBLIC_URL}/images/semiconductor.webp`,
    ppt: `${process.env.PUBLIC_URL}/docs/Rituraj_Semantic Equations_ppt.pdf`
  }
];

// --- SKELETON COMPONENT ---
const ProjectSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 flex flex-col h-full animate-pulse">
    <div className="h-56 bg-slate-200" />
    <div className="p-6 flex-1 flex flex-col">
      <div className="flex justify-between items-start mb-4">
        <div className="w-3/4 h-6 bg-slate-200 rounded" />
        <div className="w-10 h-10 bg-slate-100 rounded-lg" />
      </div>
      <div className="space-y-2 mb-6 flex-1">
        <div className="w-full h-3 bg-slate-100 rounded" />
        <div className="w-full h-3 bg-slate-100 rounded" />
        <div className="w-2/3 h-3 bg-slate-100 rounded" />
      </div>
      <div className="pt-4 border-t border-slate-50">
        <div className="w-24 h-4 bg-slate-200 rounded" />
      </div>
    </div>
  </div>
);

// --- PDF VIEWER ---
const PDFViewer = ({ file }) => {
  const [numPages, setNumPages] = useState(null);

  return (
    <div className="bg-slate-100 rounded-xl p-4 overflow-y-auto max-h-[60vh] flex flex-col items-center gap-4 border border-slate-200">
      <Document
        file={file}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        loading={<div className="flex flex-col items-center py-10 text-slate-500"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />Loading Document...</div>}
        error={<div className="text-red-500 py-10 flex flex-col items-center"><Info size={32} className="mb-2" /><span className="font-bold">Document not found.</span></div>}
        className="flex flex-col gap-4"
      >
        {Array.from(new Array(numPages), (el, index) => (
          <Page key={`page_${index + 1}`} pageNumber={index + 1} width={Math.min(650, window.innerWidth - 80)} className="shadow-lg rounded-sm" renderTextLayer={false} renderAnnotationLayer={false} canvasBackground="white" />
        ))}
      </Document>
    </div>
  );
};

const categories = ["All", ...new Set(allProjects.map(p => p.category))];

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const filteredProjects = allProjects.filter(project => {
    const matchesCategory = filter === "All" || project.category === filter;
    const matchesSearch = project.title.toLowerCase().includes(search.toLowerCase()) || 
                          project.desc.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setActiveTab('details');
  };

  const handleCloseModal = () => setSelectedProject(null);

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="container mx-auto px-6">
        
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h1 className="text-5xl font-extrabold text-slate-900 mb-6">Our Projects</h1>
          <p className="text-slate-600 text-xl">From embedded systems to high-voltage electronics, explore our community portfolio.</p>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto hide-scrollbar">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setFilter(cat)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${filter === cat ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input type="text" placeholder="Search projects..." className="w-full pl-10 pr-4 py-2 rounded-full border border-slate-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
          <AnimatePresence mode="popLayout">
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <motion.div key={`skeleton-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}><ProjectSkeleton /></motion.div>
              ))
            ) : (
              filteredProjects.map((project, idx) => (
                <motion.div key={project.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ delay: idx * 0.05 }} onClick={() => handleOpenModal(project)} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-slate-100 flex flex-col cursor-pointer">
                  {/* UPDATED: Image container now centers image and uses object-contain to fit properly */}
                  <div className="h-56 overflow-hidden relative bg-slate-50 flex items-center justify-center p-4">
                     <img 
                       src={project.image} 
                       alt={project.title} 
                       loading="lazy" 
                       className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110" 
                     />
                     <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-700 shadow-sm border border-slate-100">{project.category}</div>
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{project.title}</h3>
                      <div className="p-2 bg-slate-50 rounded-lg shrink-0">{project.icon}</div>
                    </div>
                    <p className="text-slate-600 mb-6 text-sm leading-relaxed flex-1 line-clamp-3">{project.desc}</p>
                    <div className="pt-4 border-t border-slate-50"><span className="text-blue-600 text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">View Details <ArrowRight size={16} /></span></div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" onClick={handleCloseModal}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              
              <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white z-10">
                <div className="flex items-center gap-3">
                   <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">{selectedProject.icon}</div>
                   <div><h2 className="text-xl font-bold text-slate-800 leading-tight">{selectedProject.title}</h2><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{selectedProject.category}</p></div>
                </div>
                <button onClick={handleCloseModal} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors"><X size={24} /></button>
              </div>

              <div className="flex border-b border-slate-100 bg-slate-50/50 px-6 gap-6 overflow-x-auto">
                <button onClick={() => setActiveTab('details')} className={`py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}><Info size={16} /> Overview</button>
                {selectedProject.pdf && <button onClick={() => setActiveTab('pdf')} className={`py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'pdf' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}><FileText size={16} /> Proposal</button>}
                {selectedProject.ppt && <button onClick={() => setActiveTab('ppt')} className={`py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'ppt' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}><MonitorPlay size={16} /> Presentation</button>}
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-white">
{activeTab === 'details' && (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>

    {/* --- UPDATED IMAGE SECTION --- 
        1. Changed to full width container 
        2. Increased height (h-64 on mobile, h-80 on desktop)
        3. Used object-contain so the full image is visible without cropping
    */}
    <div className="w-full h-64 md:h-80 bg-slate-50 rounded-xl border border-slate-100 mb-8 flex items-center justify-center overflow-hidden relative group">
      <img 
        src={selectedProject.image} 
        alt={selectedProject.title} 
        className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" 
      />
    </div>

    {/* --- CONTENT SECTION --- */}
    <div className="flex flex-col md:flex-row gap-8">
      
      {/* Left Column: Description */}
      <div className="flex-1 order-2 md:order-1">
        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <FileText size={20} className="text-blue-500"/> Project Description
        </h3>
        <p className="text-slate-600 leading-relaxed text-base whitespace-pre-line">
          {selectedProject.desc}
        </p>

        {/* Download Buttons (Moved here for better flow) */}
        <div className="flex flex-wrap gap-3 pt-6 mt-6 border-t border-slate-100">
           {selectedProject.pdf && (
             <a href={selectedProject.pdf} target="_blank" rel="noreferrer" className="px-5 py-2.5 bg-slate-900 hover:bg-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5">
               <Download size={16} /> Download Proposal
             </a>
           )}
           {selectedProject.ppt && (
             <a href={selectedProject.ppt} target="_blank" rel="noreferrer" className="px-5 py-2.5 bg-white border border-slate-200 hover:border-blue-500 text-slate-700 hover:text-blue-600 rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5">
               <Download size={16} /> Download PPT
             </a>
           )}
        </div>
      </div>

      {/* Right Column: Metadata (Mentors/Professors) */}
      <div className="w-full md:w-72 shrink-0 order-1 md:order-2 space-y-4">
        {selectedProject.prof && (
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
            <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Zap size={10} /> Faculty Advisor
            </p>
            <p className="font-bold text-slate-800 text-sm">{selectedProject.prof}</p>
          </div>
        )}
        {selectedProject.mentors && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Mentors</p>
            <p className="font-medium text-slate-600 text-sm leading-relaxed">{selectedProject.mentors}</p>
          </div>
        )}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Category</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white border border-slate-200 text-slate-800 shadow-sm">
              {selectedProject.category}
            </span>
        </div>
      </div>

    </div>

  </motion.div>
)}
                {activeTab === 'pdf' && selectedProject.pdf && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full"><PDFViewer file={selectedProject.pdf} /></motion.div>}
                {activeTab === 'ppt' && selectedProject.ppt && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full"><PDFViewer file={selectedProject.ppt} /></motion.div>}
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;